'use client';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';

import {WalkthroughImage} from './walkthrough-media.react-component';

import styles from './platform-showcase.module.scss';

/**
 * Dark band introducing the community platform — Figma node 5260:156728
 * (desktop), 6553:176642 (latest copy column, the 2026-09-18 refresh) and
 * 5260:160506 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * Two columns on desktop (stacked, media first, on mobile): a heading,
 * one-line description and a green-tick benefits checklist, next to the
 * community-UI media.
 *
 * The design calls for a walkthrough video there, and `WalkthroughVideo` in
 * `./walkthrough-media.react-component` implements it (poster → consent →
 * YouTube embed). The video itself does not exist yet, so the slot holds
 * `WalkthroughImage` — the same screenshot at the same size and position,
 * without the play affordance. To put the video back, swap the one call below;
 * nothing else needs to change.
 *
 * Presentational and copy-only — no props, no data.
 */
export function PlatformShowcase() {
    const {gettext} = useI18n();

    // One `gettext(…)` per row so each stays a literal msgid — see `CommunityBenefits`
    // in the reference app for the same pattern (plain strings, checked off with a
    // green tick disc).
    const benefits = [
        gettext('Join a Space centered on a clear topic or goal'),
        gettext('Gain ongoing access to an instructor you trust'),
        gettext('Share what you’re working on and get direct feedback'),
        gettext('See how others apply skills similar to yours'),
        gettext('Reply to threads and swap ideas with peers'),
    ];

    return (
        <section className={styles['showcase']} data-purpose="platform-showcase">
            <div className={styles['content']}>
                <div className={styles['media']}>
                    {/* TODO: restore <WalkthroughVideo /> here once the walkthrough video is ready. */}
                    <WalkthroughImage />
                </div>

                <div className={styles['copy']}>
                    <div className={styles['text-block']}>
                        <h2 className={styles['heading']}>
                            {gettext('Learn together. Practice together. Progress together.')}
                        </h2>
                        <p className={styles['description']}>
                            {gettext(
                                'Whatever you’re working toward, from AI to leadership, there’s a community for you.',
                            )}
                        </p>
                    </div>

                    <ul className={styles['benefit-list']}>
                        {benefits.map((benefit) => (
                            // Benefits have no stable id; order is stable within a rendered
                            // view, so the string itself is an acceptable key here.
                            <li key={benefit} className={styles['benefit']}>
                                <span className={styles['benefit-icon']}>
                                    <Icon glyph="tick" label={false} color="knockout" size="xsmall" />
                                </span>
                                <span>{benefit}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    );
}
